import { observer } from "mobx-react-lite";
import { useField, useForm } from "react-final-form";

import { Stack } from "@bps/fluent-ui";
import { Country } from "@libs/enums/country.enum.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { CountryPickerField } from "@ui-components/CountryPickerField.tsx";
import { DropdownField } from "@ui-components/form/DropdownField.tsx";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { Fieldset } from "@ui-components/form/Fieldset/Fieldset.tsx";
import { FieldSpy } from "@ui-components/form/FieldSpy.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";

import { LocationLabels } from "../PracticeScreen.types.ts";
import { LocationDetailsValues } from "./LocationDetails.types.ts";

const nameOf = nameOfFactory<LocationDetailsValues>();

export const LocationDetailsFormAddressFields = observer(() => {
  const form = useForm<LocationDetailsValues>();
  const {
    input: { value: country }
  } = useField<LocationDetailsValues["country"]>(nameOf("country"), {
    subscription: { value: true }
  });

  const { core } = useStores();

  const { countries } = core.ref;

  const isStateRequired = (value: string) =>
    countries.values.some(c => c.code === value && c.isStateRequired);

  const isCityRequired = (value: string) =>
    countries.values.some(c => c.code === value && !c.isStateRequired);

  return (
    <Fieldset legend={LocationLabels.address} hasAsterisk>
      <Stack tokens={{ childrenGap: 8 }}>
        <TextInputField
          placeholder={
            country === Country.NewZealand
              ? LocationLabels.street
              : LocationLabels.addressLine1
          }
          name={nameOf("address1")}
        />
        <TextInputField
          placeholder={
            country === Country.NewZealand
              ? LocationLabels.buildingName
              : LocationLabels.addressLine2
          }
          name={nameOf("address2")}
        />
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <TextInputField
            placeholder={LocationLabels.suburb}
            name={nameOf("suburb")}
          />
          <FieldCondition when={nameOf("country")} is={isCityRequired}>
            <TextInputField
              placeholder={
                country === Country.NewZealand
                  ? LocationLabels.townCity
                  : LocationLabels.city
              }
              name={nameOf("city")}
            />
          </FieldCondition>
          <FieldCondition when={nameOf("country")} is={isStateRequired}>
            <DropdownField
              name={nameOf("state")}
              placeholder={LocationLabels.state}
              options={core.ref.australianStates.keyTextValues}
            />
          </FieldCondition>
          <FieldSpy
            name={nameOf("country")}
            onChange={country =>
              country &&
              !isStateRequired(country) &&
              form.change("state", undefined)
            }
          />
        </Stack>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <Stack.Item styles={{ root: { flex: 1 } }}>
            <TextInputField
              placeholder={LocationLabels.postCode}
              name={nameOf("postCode")}
            />
          </Stack.Item>
          <Stack.Item styles={{ root: { flex: 1 } }}>
            <FieldSpy
              name={nameOf("country")}
              onChange={value => {
                if (!value) {
                  form.change(nameOf("country"), Country.NewZealand);
                }
              }}
            />
            <CountryPickerField
              name={nameOf("country")}
              placeholder={LocationLabels.country}
              calloutProps={{
                calloutMaxHeight: 500
              }}
            />
          </Stack.Item>
        </Stack>
      </Stack>
    </Fieldset>
  );
});
